<script setup lang="ts">
  import { useMainHeaderStore } from '@/components/main-header/store'
  import useHeaderDataLayer from '@/composables/data-layers/useHeaderDataLayer'
  import type {
    IHeaderProfile,
    INavigation,
    IHeaderSection,
    IHeaderTabs,
    ITopBarData,
    IHeaderMobileAppLink
  } from '@/components/main-header/types'

  const props = defineProps<{
    headerData: {
      tabs: IHeaderTabs[]
      sections: IHeaderSection[]
      mobileAppLinks: IHeaderMobileAppLink[]
    }
  }>()

  const headerTabs = computed(() =>
    props.headerData.tabs.map((tab) => ({
      ...tab,
      isActive: activeTab.value === tab.key
    }))
  )

  const { isDesktop } = useBTBreakpoints()

  const { locale } = useI18n()

  const { data, error } = await useFetch<ITopBarData>('/api/storyblok/topbar', {
    query: { language: locale.value }
  })

  const mainHeaderStore = useMainHeaderStore()
  const { pushHeaderTabClickDataLayer, getHeaderTabEventCategory } = useHeaderDataLayer()

  const { logo, activeTab } = storeToRefs(mainHeaderStore)

  const navigation = ref<INavigation[] | null>(null)
  const profile = ref<IHeaderProfile | null>(null)

  if (!error.value) {
    mainHeaderStore.logo = data.value?.logo ? data.value.logo : null
    navigation.value = data.value?.navigation ? data.value.navigation : null
    profile.value = data.value?.profile ? data.value.profile : null
  }

  const showComponent = (component: string) => {
    if (component === 'header-search') {
      return isDesktop.value
    }

    return true
  }

  function onTabClick(tabKey: string) {
    pushHeaderTabClickDataLayer(getHeaderTabEventCategory(tabKey))
    activeTab.value = tabKey
  }
</script>

<template>
  <div class="flex items-center justify-between">
    <div class="flex items-center gap-x-14">
      <MainHeaderLogo
        v-if="logo"
        :logo="logo"
      />

      <div
        v-if="isDesktop"
        class="flex h-19 items-stretch gap-x-8"
      >
        <NuxtLink
          v-for="tab in headerTabs"
          :key="tab.key"
          :href="tab.url"
          :aria-current="tab.isActive ? 'page' : undefined"
          class="relative flex h-full items-center leading-8 no-underline"
          external
          no-prefetch
          @click="onTabClick(tab.key)"
        >
          <span
            :class="{
              'font-semibold text-subtitle-1': tab.isActive,
              'text-body-1': !tab.isActive
            }"
          >
            {{ tab.label }}
          </span>

          <span
            v-if="tab.isActive"
            class="absolute inset-x-0 bottom-0 h-1.5 rounded-t-full bg-bt-green"
          />
        </NuxtLink>
      </div>
    </div>

    <div class="mr-2 flex items-center justify-end sm:gap-x-4 md:mr-10">
      <div class="flex items-center gap-x-2 md:gap-x-4">
        <template
          v-for="(item, itemIndex) in navigation"
          :key="`${item.component}_${itemIndex}`"
        >
          <component
            :is="item.component"
            v-if="showComponent(item.component)"
            :item="item"
          />
        </template>
      </div>

      <LazyHeaderProfile
        v-if="profile && isDesktop"
        :profile="profile"
      />

      <LazyMainHeaderMegaMenu v-if="isDesktop" />

      <LazyMainHeaderMobileSidebar
        v-if="!isDesktop"
        :sidebar-data="headerData"
      />
    </div>
  </div>
</template>
